import { useEffect, useState } from "react"

import { Orb } from "@/components/landing/discovery/orb"
import { PurchaseBar } from "@/components/landing/discovery/purchase-bar"
import { type JobSlug, siteLabel } from "@/components/landing/discovery/script"
import { TestAgentConversation } from "@/components/landing/discovery/test-agent-conversation"
import { useAssessmentLookup } from "@/components/landing/discovery/use-assessment-lookup"
import { Brand } from "@/components/landing/hub/brand"
import { type JobOffer, type PartnerInfo } from "@/components/landing/hub/types"
import { partnerLandingPageAssessmentCompletePath } from "@/routes"

/** IMP-504: what the demo knows when Get started is tapped — routes the
 *  purchase into the expedited existing-account checkout when possible. */
export interface AssessmentPurchaseContext {
  accountLandingUid: string | null
  email: string | null
}

function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  )
}

/**
 * IMP-531: the assistant demo — a full-screen overlay launched from the URL
 * field, deliberately not a page, so the partner's site stays a landing page.
 *
 * Interim replacement for the discovery assessment (IMP-502..506 stay the
 * destination): Iza trains on the visitor's site and hands them a test call,
 * with no interview. Email capture completes the record server-side (account
 * creation, briefed-lead email — IMP-504/505 unchanged).
 *
 * Not portaled: it renders inside .lp-root so the partner accent tokens
 * reach it, and its entrance (.lp-overlay-in) animates opacity only, so its
 * fixed descendants anchor to the viewport.
 */
export function AssessmentOverlay({
  landingUid,
  website,
  assessmentUid,
  partner,
  jobs,
  onGetStarted,
  onClose,
}: {
  landingUid: string
  website: string
  /** Created at URL-submit time, after the server's pre-screen passed. */
  assessmentUid: string
  partner: PartnerInfo
  /** The hub's job offers; only purchasable ones reach the purchase bar. */
  jobs: JobOffer[]
  /** IMP-504: once the demo has created its account, the context routes
   *  Get started into the expedited existing-account checkout, with the
   *  captured email carried through. */
  onGetStarted: (job: JobOffer, context: AssessmentPurchaseContext) => void
  onClose: () => void
}) {
  const [purchaseOpen, setPurchaseOpen] = useState(false)
  // IMP-504: set by the demo call (which provisions the account on first
  // demand) or by completion — whichever the visitor reaches first.
  const [accountLandingUid, setAccountLandingUid] = useState<string | null>(
    null,
  )
  const [email, setEmail] = useState<string | null>(null)
  // IMP-503: the real site lookup, running behind the conversation.
  const lookup = useAssessmentLookup(landingUid, assessmentUid)
  const site = siteLabel(website)

  // IMP-504: email capture completes the demo server-side — the account is
  // created (or claimed), the record attaches to it, and the emails go out.
  // No interview means no answers; the record carries none.
  function handleEmail(value: string) {
    setEmail(value)
    if (!lookup.assessmentUid) return
    void fetch(
      partnerLandingPageAssessmentCompletePath(
        landingUid,
        lookup.assessmentUid,
      ),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ email: value, answers: {} }),
      },
    )
      .then(async (response) => {
        if (!response.ok) return
        const data = (await response.json()) as {
          account_landing_uid: string | null
        }
        if (data.account_landing_uid)
          setAccountLandingUid(data.account_landing_uid)
      })
      .catch(() => {
        // The conversation already moved on; completion failing is invisible
        // here and the partner-side record simply stays unfinished.
      })
  }

  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [])

  useEffect(() => {
    // Capture phase, and a DOM check rather than a prop: when the purchase
    // modal is stacked on top, its own Escape handling closes it and React
    // re-renders in the microtask BETWEEN listeners of the same event — a
    // prop-gated listener re-attaches mid-propagation and catches the very
    // Escape that closed the modal. Window capture runs before the dialog
    // acts, so it still sees the dialog in the DOM and yields to it.
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return
      if (document.querySelector('[role="dialog"]')) return
      onClose()
    }
    window.addEventListener("keydown", onKey, true)
    return () => window.removeEventListener("keydown", onKey, true)
  }, [onClose])

  const purchasable = jobs.filter((job) => job.pricing != null)
  // IMP-531: the recommendation is the receptionist — the job the demo just
  // performed. (The interview-driven recommendation left with the interview.)
  const recommended = (purchasable.find((job) => job.slug === "receptionist")
    ?.slug ?? purchasable[0]?.slug) as JobSlug | undefined

  return (
    <div className="bg-lp-bg lp-overlay-in fixed inset-0 z-50 overflow-y-auto">
      <header className="bg-lp-bg/85 sticky top-0 z-10 backdrop-blur-sm">
        <div className="mx-auto flex h-16 w-full max-w-2xl items-center justify-between px-4">
          <Brand partner={partner} />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-lp-muted hover:text-lp-fg hover:bg-lp-surface flex size-9 items-center justify-center rounded-full text-xl transition-colors"
          >
            ×
          </button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 pt-10 pb-24 sm:pt-16">
        <Orb />

        <div className="lp-msg-in mt-9 w-full">
          <TestAgentConversation
            landingUid={landingUid}
            site={site}
            lookup={lookup}
            partner={partner}
            purchasable={purchasable.length > 0}
            onEmail={handleEmail}
            onAccountLanding={setAccountLandingUid}
            onPurchaseOpen={() => setPurchaseOpen(true)}
          />
        </div>
      </main>

      {/* Opens at the post-call email ask — the value peak — with the
          recommended agent and price only. Never on the failed path, and
          never for a partner with nothing purchasable. */}
      {purchaseOpen && purchasable.length > 0 && recommended && (
        <PurchaseBar
          jobs={purchasable}
          recommended={recommended}
          onGetStarted={(job) =>
            onGetStarted(job, { accountLandingUid, email })
          }
        />
      )}
    </div>
  )
}
